import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { campaignService, branchService } from '../services';
import { Campaign, Branch } from '../types';
import { Pagination } from '../components/Pagination';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Loader2, Megaphone, Calendar, Edit, Trash2, Eye } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';

interface CreateCampaignFormData {
  branchId: string;
  title: string;
  description?: string;
  startDate: string;
  endDate: string;
  additionalCommissionRate?: number;
  additionalFixedCommission?: number;
  minPeopleRequired?: number;
}

export const CampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CreateCampaignFormData>();

  useEffect(() => {
    loadCampaigns(currentPage, pageSize);
    loadBranches();
  }, [currentPage, pageSize]);

  const loadCampaigns = async (page: number = 0, size: number = 10) => {
    try {
      setLoading(true);
      setError(null);
      const response = await campaignService.getAllCampaigns(page, size);
      setCampaigns(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const response = await branchService.getAllBranches(0, 1000);
      setBranches(response.content);
    } catch (err: any) {
      console.error('Failed to load branches:', err);
    }
  };

  const handleOpenDialog = () => {
    reset({
      branchId: '',
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      additionalCommissionRate: 0,
      additionalFixedCommission: 0,
      minPeopleRequired: 1,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset({});
  };

  const onSubmit = async (data: CreateCampaignFormData) => {
    try {
      const createRequest = {
        branchId: parseInt(data.branchId),
        title: data.title,
        description: data.description,
        startDate: data.startDate,
        endDate: data.endDate,
        additionalCommissionRate: data.additionalCommissionRate ?? 0,
        additionalFixedCommission: data.additionalFixedCommission ?? 0,
        minPeopleRequired: data.minPeopleRequired ?? 1,
      };

      await campaignService.createCampaign(createRequest);
      await loadCampaigns(currentPage, pageSize);
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to create campaign');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        await campaignService.deleteCampaign(String(id));
        await loadCampaigns(currentPage, pageSize);
      } catch (err: any) {
        setError(err.message || 'Failed to delete campaign');
      }
    }
  };

  if (loading && campaigns.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Campaigns</h1>
          <p className="text-muted-foreground">Manage marketing campaigns and promotions</p>
        </div>
        <Button onClick={handleOpenDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Create Campaign
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Campaigns</CardTitle>
          <CardDescription>List of all marketing campaigns</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign Name</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Megaphone className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{campaign.title}</p>
                        {campaign.description && (
                          <p className="text-sm text-muted-foreground">{campaign.description}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {campaign.branch?.name || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Calendar className="h-3 w-3" />
                      {campaign.startDate && campaign.endDate ? (
                        <>
                          {new Date(campaign.startDate).toLocaleDateString()} -{' '}
                          {new Date(campaign.endDate).toLocaleDateString()}
                        </>
                      ) : (
                        'No dates set'
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <span>{campaign.additionalCommissionRate}%</span>
                      {campaign.additionalFixedCommission > 0 && (
                        <span> + ${campaign.additionalFixedCommission}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={campaign.active ? 'default' : 'secondary'}>
                      {campaign.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/campaigns/${campaign.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(campaign.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {campaigns.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">No campaigns found</p>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            itemName="campaigns"
          />
        </CardContent>
      </Card>

      {/* Create Campaign Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Campaign</DialogTitle>
            <DialogDescription>
              Add a new marketing campaign with special commission rates
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="branchId">Branch *</Label>
                <Controller
                  name="branchId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a branch" />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((branch) => (
                          <SelectItem key={branch.id} value={String(branch.id)}>
                            {branch.name} - {branch.organizationName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.branchId && <p className="text-sm text-destructive">Branch is required</p>}
              </div>

              <div className="col-span-2">
                <Label htmlFor="title">Campaign Title *</Label>
                <Input id="title" {...register('title', { required: true })} />
                {errors.title && <p className="text-sm text-destructive">Title is required</p>}
              </div>

              <div className="col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  placeholder="Describe the campaign and its benefits..."
                />
              </div>

              <div>
                <Label htmlFor="startDate">Start Date *</Label>
                <Input id="startDate" type="date" {...register('startDate', { required: true })} />
                {errors.startDate && (
                  <p className="text-sm text-destructive">Start date is required</p>
                )}
              </div>

              <div>
                <Label htmlFor="endDate">End Date *</Label>
                <Input id="endDate" type="date" {...register('endDate', { required: true })} />
                {errors.endDate && <p className="text-sm text-destructive">End date is required</p>}
              </div>

              <div>
                <Label htmlFor="additionalCommissionRate">Additional Commission Rate (%)</Label>
                <Input
                  id="additionalCommissionRate"
                  type="number"
                  step="0.01"
                  defaultValue={0}
                  {...register('additionalCommissionRate')}
                  placeholder="e.g., 5.00"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Extra percentage commission for this campaign
                </p>
              </div>

              <div>
                <Label htmlFor="additionalFixedCommission">Additional Fixed Commission ($)</Label>
                <Input
                  id="additionalFixedCommission"
                  type="number"
                  step="0.01"
                  defaultValue={0}
                  {...register('additionalFixedCommission')}
                  placeholder="e.g., 10.00"
                />
                <p className="text-xs text-muted-foreground mt-1">Extra fixed amount per booking</p>
              </div>

              <div className="col-span-2">
                <Label htmlFor="minPeopleRequired">Minimum People Required</Label>
                <Input
                  id="minPeopleRequired"
                  type="number"
                  defaultValue={1}
                  {...register('minPeopleRequired')}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Minimum number of people for campaign to apply
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">Create Campaign</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignsPage;
